import React from 'react';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  EcosystemIcon,
  type Column,
} from '@redbax/ui-kit';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { PackageVulnerability } from '@redbax/shared';
import { useVulnerabilitySearch, useRecentVulnerabilities } from '../api/package-policy-hooks';
import { SeverityBadge } from '../components/severity-badge';
import { Link } from 'react-router';

const ALL = '__all__';
const ecosystems = [ALL, 'npm', 'PyPI', 'NuGet', 'Maven', 'Go', 'RubyGems', 'crates.io'] as const;
const severities = [ALL, 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
const PAGE_SIZE = 50;

function formatAffectedVersions(raw: string | null | undefined): string {
  if (!raw) return '-';
  try {
    const parsed = JSON.parse(raw);
    const ranges = parsed.ranges as { events?: { introduced?: string; fixed?: string }[] }[] | undefined;
    if (ranges?.length) {
      const parts = ranges.flatMap((r) =>
        (r.events ?? []).reduce<string[]>((acc, e) => {
          if (e.introduced) acc.push(`>=${e.introduced}`);
          if (e.fixed) acc[acc.length - 1] = `${acc[acc.length - 1] ?? ''}, <${e.fixed}`;
          return acc;
        }, []),
      );
      if (parts.length) return parts.join('; ');
    }
    const versions = parsed.versions as string[] | undefined;
    if (versions?.length) return versions.join(', ');
  } catch { /* ignore */ }
  return '-';
}

const columns: Column<PackageVulnerability>[] = [
  {
    key: 'packageName',
    header: 'Package',
    sortable: true,
    render: (row) => (
      <div className="flex items-center gap-2 max-w-[250px]">
        <div className="flex-1 min-w-0">
          <Link
            to={`/package-security/packages/${encodeURIComponent(row.ecosystem)}/${encodeURIComponent(row.packageName)}`}
            className="block truncate text-sm font-medium text-cyan-info hover:underline"
            title={row.packageName}
          >
            {row.packageName}
          </Link>
          <span className="block truncate text-xs text-text-secondary" title={row.id}>
            {row.id}
          </span>
        </div>
        <span title={row.ecosystem} className="flex-shrink-0">
          <EcosystemIcon ecosystem={row.ecosystem} size={18} />
        </span>
      </div>
    ),
  },
  {
    key: 'summary',
    header: 'Summary',
    render: (row) => (
      <span className="block max-w-[300px] truncate text-sm text-text-secondary" title={row.summary ?? ''}>
        {row.summary ?? '-'}
      </span>
    ),
  },
  {
    key: 'severity',
    header: 'Severity',
    sortable: true,
    render: (row) => <SeverityBadge severity={row.severity} />,
  },
  {
    key: 'affectedVersions',
    header: 'Affected Versions',
    render: (row) => {
      const text = formatAffectedVersions(row.affectedVersions);
      return (
        <span className="block max-w-[200px] truncate text-xs text-text-secondary" title={text}>
          {text}
        </span>
      );
    },
  },
];

export function VulnerabilitySearchPage() {
  const [ecosystem, setEcosystem] = React.useState(ALL);
  const [packageName, setPackageName] = React.useState('');
  const [severity, setSeverity] = React.useState(ALL);
  const [offset, setOffset] = React.useState(0);
  const [searchParams, setSearchParams] = React.useState<{
    ecosystem?: string;
    packageName?: string;
    severity?: string;
    offset?: number;
    limit?: number;
  }>({});

  const { data, isLoading, isFetching } = useVulnerabilitySearch(searchParams);
  const { data: recentData, isLoading: recentLoading } = useRecentVulnerabilities(10);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setSearchParams({
      ecosystem: ecosystem !== ALL ? ecosystem : undefined,
      packageName: packageName.trim() || undefined,
      severity: severity !== ALL ? severity : undefined,
      offset: 0,
      limit: PAGE_SIZE,
    });
  };

  const handlePageChange = (newOffset: number) => {
    setOffset(newOffset);
    setSearchParams((prev) => ({ ...prev, offset: newOffset, limit: PAGE_SIZE }));
  };

  const hasSearchParams = !!(searchParams.ecosystem || searchParams.packageName || searchParams.severity);
  const totalCount = data?.totalCount ?? 0;
  const items = data?.items ?? [];
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div>
      <PageHeader
        title="Vulnerability Search"
        description="Search the vulnerability database for known package vulnerabilities"
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Search Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-4">
            <div>
              <Label htmlFor="vuln-ecosystem">Ecosystem</Label>
              <Select value={ecosystem} onValueChange={setEcosystem}>
                <SelectTrigger className="mt-1 w-40">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {ecosystems.map((eco) => (
                    <SelectItem key={eco} value={eco}>
                      {eco === ALL ? 'All' : eco}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="vuln-package">Package Name</Label>
              <Input
                id="vuln-package"
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
                placeholder="e.g., lodash"
                className="mt-1 w-56"
              />
            </div>
            <div>
              <Label htmlFor="vuln-severity">Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger className="mt-1 w-36">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {severities.map((sev) => (
                    <SelectItem key={sev} value={sev}>
                      {sev === ALL ? 'All' : sev}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={isFetching}>
              <Search className="mr-1 h-4 w-4" />
              {isFetching ? 'Searching...' : 'Search'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {hasSearchParams
                ? `Results ${totalCount > 0 ? `(${totalCount.toLocaleString()})` : ''}`
                : 'Recent Vulnerabilities'}
            </CardTitle>
            {hasSearchParams && totalPages > 1 && (
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <span>Page {currentPage} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(offset - PAGE_SIZE)}
                  disabled={offset === 0 || isFetching}
                  className="h-7 w-7 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= totalCount || isFetching}
                  className="h-7 w-7 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={hasSearchParams ? items : (recentData?.items ?? [])}
            keyExtractor={(row) => `${row.id}-${row.ecosystem}-${row.packageName}`}
            loading={hasSearchParams ? isLoading : recentLoading}
            emptyMessage={hasSearchParams ? 'No vulnerabilities found matching your criteria' : 'No vulnerabilities in database'}
          />
        </CardContent>
      </Card>
    </div>
  );
}
